import { sanitizeNullStrings } from "@/utils/sanitize";
import { useEffect, useMemo, useState } from "react";
import {
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Divider,
	FormControlLabel,
	Grid2,
	IconButton,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import type { RollApplication } from "../models/RollApplication";
import { CHEMICAL_FIELDS } from "../models/RollApplication";
import { RollMaterialSelector } from "./RollMaterialSelector";

interface FormErrors {
	Name?: string;
}

interface RollApplicationDetailViewerProps {
	application: RollApplication | null;
	existingApplications: RollApplication[];
	isSaving: boolean;
	onSave: (app: RollApplication) => void;
	onClose: () => void;
}

const sectionHeaderSx = {
	color: "primary.main",
	fontWeight: 600,
	fontSize: "0.85rem",
	mt: 1.5,
	mb: 0.5,
};

export const RollApplicationDetailViewer = ({
	application,
	existingApplications,
	isSaving,
	onSave,
	onClose,
}: RollApplicationDetailViewerProps) => {
	const [formData, setFormData] = useState<RollApplication | null>(null);
	const [errors, setErrors] = useState<FormErrors>({});
	const [touched, setTouched] = useState<Record<string, boolean>>({});

	const isNewApplication = !application?.Id;

	const validateForm = useMemo(() => {
		return (data: RollApplication | null): FormErrors => {
			const newErrors: FormErrors = {};

			if (!data?.Name || data.Name.trim().length === 0) {
				newErrors.Name = "Name is required";
			} else {
				const isDuplicate = existingApplications.some(
					(a) =>
						a.Id !== data.Id &&
						a.Name.toLowerCase() === data.Name.trim().toLowerCase(),
				);
				if (isDuplicate) {
					newErrors.Name = "This name already exists";
				}
			}

			return newErrors;
		};
	}, [existingApplications]);

	const formErrors = useMemo(
		() => validateForm(formData),
		[formData, validateForm],
	);
	const isFormValid = Object.keys(formErrors).length === 0;
	const canCreate = isNewApplication && isFormValid;

	useEffect(() => {
		if (application) {
			setFormData(sanitizeNullStrings({ ...application }));
		} else {
			setFormData(null);
		}
		setErrors({});
		setTouched({});
	}, [application]);

	const handleChange = (field: keyof RollApplication, value: unknown) => {
		setFormData((prev) => (prev ? { ...prev, [field]: value } : null));
	};

	const handleBlur = (field: keyof RollApplication) => {
		setTouched((prev) => ({ ...prev, [field]: true }));
		if (formData) {
			setErrors(validateForm(formData));
		}
	};

	const handleBlurSave = (field: keyof RollApplication, value: unknown) => {
		handleBlur(field);

		if (isNewApplication) return;

		const updatedData = formData ? { ...formData, [field]: value } : null;
		const currentErrors = validateForm(updatedData);
		if (currentErrors[field as keyof FormErrors]) return;

		const originalValue = application?.[field];
		if (originalValue !== value) {
			onSave({ ...formData!, [field]: value });
		}
	};

	const handleSelectChange = (field: keyof RollApplication, value: unknown) => {
		handleChange(field, value);
		setTouched((prev) => ({ ...prev, [field]: true }));

		const updatedData = formData ? { ...formData, [field]: value } : null;
		const currentErrors = validateForm(updatedData);
		setErrors(currentErrors);

		if (isNewApplication) return;

		if (application?.[field] !== value) {
			onSave({ ...formData!, [field]: value });
		}
	};

	const handleNumericBlurSave = (
		field: keyof RollApplication,
		rawValue: string,
		max?: number,
	) => {
		const value = rawValue === "" ? null : Number.parseFloat(rawValue);
		if (value != null && value < 0) return;
		if (max != null && value != null && value > max) return;
		handleChange(field, value);
		handleBlurSave(field, value);
	};

	const handleCreate = () => {
		setTouched({ Name: true });
		setErrors(formErrors);

		if (formData && canCreate) {
			onSave(formData);
		}
	};

	if (!application) {
		return (
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: "100%",
					color: "text.secondary",
				}}
			>
				<Typography>Select a roll application to view details</Typography>
			</Box>
		);
	}

	if (!formData) return null;

	return (
		<Box sx={{ p: 1.5, height: "100%", overflow: "auto" }}>
			{/* Header */}
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					mb: 1,
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
						{isNewApplication ? "New Roll Application" : formData.Name}
					</Typography>
					{isSaving && <CircularProgress size={16} />}
				</Box>
				<Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
					{isNewApplication && (
						<Button
							color="primary"
							variant="contained"
							size="small"
							onClick={handleCreate}
							disabled={!canCreate || isSaving}
						>
							{isSaving ? "CREATING..." : "CREATE"}
						</Button>
					)}
					<Tooltip title="Close">
						<IconButton size="small" onClick={onClose} disabled={isSaving}>
							<CloseIcon />
						</IconButton>
					</Tooltip>
				</Box>
			</Box>

			<Divider sx={{ mb: 1 }} />

			<Grid2 container spacing={1}>
				{/* Basic Information */}
				<Grid2 size={{ xs: 12 }}>
					<Typography sx={sectionHeaderSx}>Basic Information</Typography>
				</Grid2>
				<Grid2 size={{ xs: 12, md: 6 }}>
					<TextField
						label="Name"
						value={formData.Name}
						onChange={(e) => handleChange("Name", e.target.value)}
						onBlur={(e) => handleBlurSave("Name", e.target.value)}
						fullWidth
						size="small"
						required
						error={touched.Name && !!errors.Name}
						helperText={touched.Name && errors.Name}
						slotProps={{ inputLabel: { shrink: true } }}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, md: 6 }}>
					<RollMaterialSelector
						value={formData.RollMaterialId}
						onChange={(value) => handleSelectChange("RollMaterialId", value)}
						fullWidth
						size="small"
					/>
				</Grid2>

				{/* Hardness Specifications */}
				<Grid2 size={{ xs: 12 }}>
					<Typography sx={sectionHeaderSx}>Hardness Specifications</Typography>
				</Grid2>
				<Grid2 size={{ xs: 12, md: 6 }}>
					<TextField
						label="Maximum Hardness"
						type="number"
						value={formData.MaximumHardness ?? ""}
						onChange={(e) =>
							handleChange(
								"MaximumHardness",
								e.target.value === ""
									? null
									: Number.parseFloat(e.target.value),
							)
						}
						onBlur={(e) =>
							handleNumericBlurSave("MaximumHardness", e.target.value)
						}
						fullWidth
						size="small"
						slotProps={{
							inputLabel: { shrink: true },
							htmlInput: { step: 1 },
						}}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, md: 6 }}>
					<TextField
						label="Minimum Hardness"
						type="number"
						value={formData.MinimumHardness ?? ""}
						onChange={(e) =>
							handleChange(
								"MinimumHardness",
								e.target.value === ""
									? null
									: Number.parseFloat(e.target.value),
							)
						}
						onBlur={(e) =>
							handleNumericBlurSave("MinimumHardness", e.target.value)
						}
						fullWidth
						size="small"
						slotProps={{
							inputLabel: { shrink: true },
							htmlInput: { step: 1 },
						}}
					/>
				</Grid2>

				{/* Application Comments */}
				<Grid2 size={{ xs: 12 }}>
					<Typography sx={sectionHeaderSx}>Comments</Typography>
				</Grid2>
				<Grid2 size={{ xs: 12 }}>
					<TextField
						label="Application Comments"
						value={formData.ApplicationComments ?? ""}
						onChange={(e) =>
							handleChange("ApplicationComments", e.target.value || null)
						}
						onBlur={(e) =>
							handleBlurSave("ApplicationComments", e.target.value || null)
						}
						fullWidth
						size="small"
						multiline
						rows={3}
						slotProps={{ inputLabel: { shrink: true } }}
					/>
				</Grid2>

				{/* Chemical Composition */}
				<Grid2 size={{ xs: 12 }}>
					<Typography sx={sectionHeaderSx}>Chemical Composition</Typography>
				</Grid2>
				{CHEMICAL_FIELDS.map((field) => {
					const fieldValue = formData[field as keyof RollApplication] as
						| number
						| null;
					const outOfRange =
						fieldValue != null && (fieldValue < 0 || fieldValue > 99.999);
					return (
						<Grid2 key={field} size={{ xs: 6, sm: 4, md: 2.4 }}>
							<TextField
								label={field}
								type="number"
								value={fieldValue ?? ""}
								onChange={(e) =>
									handleChange(
										field as keyof RollApplication,
										e.target.value === ""
											? null
											: Number.parseFloat(e.target.value),
									)
								}
								onBlur={(e) =>
									handleNumericBlurSave(
										field as keyof RollApplication,
										e.target.value,
										99.999,
									)
								}
								fullWidth
								size="small"
								error={outOfRange}
								helperText={outOfRange ? "Range: 0 - 99.999" : undefined}
								slotProps={{
									inputLabel: { shrink: true },
									htmlInput: { step: 0.001, min: 0, max: 99.999 },
								}}
							/>
						</Grid2>
					);
				})}

				{/* Active */}
				<Grid2 size={{ xs: 12 }}>
					<FormControlLabel
						control={
							<Checkbox
								checked={formData.Active}
								onChange={(e) => handleSelectChange("Active", e.target.checked)}
								size="small"
							/>
						}
						label="Active"
						sx={{ ml: 0, mt: 1 }}
					/>
				</Grid2>
			</Grid2>
		</Box>
	);
};
